import { BellRing } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isStandalone, useAuth, usePushSubscription } from '@/hooks';
import { Button } from './Button';
import { Modal, ModalFooter } from './Modal';

/**
 * "Ne pitaj više" is per account, not per device: on a shared phone one
 * colleague's opt-out must not silence the offer for everyone after them.
 */
const neverAskKey = (userId: string) => `push_prompt_never:${userId}`;

/**
 * "Ne sada" holds for the current app launch only. sessionStorage is the
 * launch boundary: it survives the reloads that happen inside one launch
 * (pull-to-refresh, the post-deploy chunk-recovery reload, a deep-link
 * navigation into the running window) but not a fresh window, so the next
 * standalone open asks again (issue #36 wants the offer repeated until the
 * user either enables push or opts out for good). Keyed per account for the
 * same reason as the opt-out: a logout does not end the window, so one
 * colleague's "Ne sada" must not hold for the next login.
 */
const snoozeKey = (userId: string) => `push_prompt_snoozed:${userId}`;

/**
 * Offers push notifications when the app runs as an installed PWA (issue #36).
 * The profile pages keep the manual toggle; this modal exists because nobody
 * finds that toggle on their own. It stays away from regular browser tabs:
 * there the install banner is the right offer, not a permission ask.
 */
export function PushPromptModal() {
  const { t } = useTranslation('notifications');
  const { user } = useAuth();
  const { isSupported, isSubscribed, isChecking, isPending, hasKey, toggle } =
    usePushSubscription();
  const [snoozed, setSnoozed] = useState(
    () => !!user && sessionStorage.getItem(snoozeKey(user.id)) === 'true'
  );

  const open =
    isStandalone() &&
    isSupported &&
    hasKey &&
    !isChecking &&
    !isSubscribed &&
    !snoozed &&
    !!user &&
    Notification.permission !== 'denied' &&
    localStorage.getItem(neverAskKey(user.id)) !== 'true';

  const snooze = () => {
    if (user) sessionStorage.setItem(snoozeKey(user.id), 'true');
    setSnoozed(true);
  };

  const neverAsk = () => {
    if (user) localStorage.setItem(neverAskKey(user.id), 'true');
    setSnoozed(true);
  };

  if (!open) return null;

  return (
    <Modal
      open
      onOpenChange={(next) => {
        if (!next) snooze();
      }}
      title={t('push.promptTitle')}
    >
      <div data-testid="push-prompt-modal" className="space-y-4">
        <div className="flex items-start gap-3">
          <BellRing className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
          <p className="text-sm text-admin-text-secondary">{t('push.promptBody')}</p>
        </div>
        <ModalFooter className="flex-wrap">
          <Button variant="ghost" size="sm" onClick={neverAsk} data-testid="push-prompt-never">
            {t('push.dontAskAgain')}
          </Button>
          <Button variant="secondary" size="sm" onClick={snooze} data-testid="push-prompt-not-now">
            {t('push.notNow')}
          </Button>
          <Button size="sm" onClick={toggle} loading={isPending} data-testid="push-prompt-enable">
            {t('push.promptEnable')}
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
}
